import { computed } from 'vue'
import { useTenantConfig } from './useTenantConfig'

const { config } = useTenantConfig()

function deepMerge(target, source) {
    const output = { ...target }
    if (!source) return output
    for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            output[key] = deepMerge(target[key] || {}, source[key])
        } else {
            output[key] = source[key]
        }
    }
    return output
}

/**
 * The palette every tenant theme is deep-merged onto.
 *
 * Two kinds of key live here:
 *
 *   LEGACY keys carry a real default colour. They are what production tenant
 *   JSON has always stored.
 *
 *   SEMANTIC keys (theme decoupling P2, 2026-09) default to `null`, and
 *   `null` means DERIVED: useThemeTokens.js computes the value from the legacy
 *   keys the element was painted with before the key existed — e.g.
 *   `chrome.start ?? brand.overlay`, `cta.bg ?? brand.primary`,
 *   `status.success ?? mix(green, brand.accent)`. A tenant that never sets them
 *   renders byte-for-byte as before; a tenant that sets one overrides only that
 *   element family. The derivation for each key is documented next to its CSS
 *   variable in useThemeTokens.js.
 *
 * Since P7 this file derives NOTHING from the palette: useThemeTokens.js is the
 * only consumer of both kinds of key.
 *
 * Semantic groups / leaves (23):
 *   chrome        start, mid, end, link, linkHover, text, shadow   header + footer bars
 *   cta           bg, fg, gradientStart, gradientEnd, gradientText solid + gradient buttons
 *   announcement  start, end, text                                 announcement modal band
 *   text          heading, link, onCard
 *   status        tint, success, danger, warning                   (closed stays legacy)
 *   surface       backdrop                                         sheet / modal scrim
 */
const defaultPalette = {
    brand: {
        primary: '#0A1A2F',
        secondary: '#3A6EA5',
        accent: '#6FA8FF',
        overlay: '#B3D4FF',
        backgroundHeader: null,
        backgroundFooter: null,
        backgroundPriceSummary: null
    },
    text: {
        primary: '#0A1A2F',
        subtitle: '#0A1A2F',
        secondary: '#3A6EA5',
        muted: '#7A8CA3',
        onBrand: '#000000ff',
        strong: '#000000',
        heading: null,
        link: null,
        onCard: null,
    },
    surface: {
        card: '#FFFFFF',
        page: '#F5F9FF',
        input: '#E6EEF8',
        hover: '#DCE8F5',
        gradientMid: '#EAF3FF',
        gradientBottom: '#D4E6FF',
        backdrop: null,
    },
    border: {
        main: '#C4D4E6',
        light: '#D7E3F1',
        featured: '#3385FF',
    },
    status: {
        closed: '#A5B4C8',
        tint: null,
        success: null,
        danger: null,
        warning: null,
    },
    chrome: {
        start: null,
        mid: null,
        end: null,
        link: null,
        linkHover: null,
        text: null,
        shadow: null,
    },
    cta: {
        bg: null,
        fg: null,
        gradientStart: null,
        gradientEnd: null,
        gradientText: null,
    },
    announcement: {
        start: null,
        end: null,
        text: null,
    },
    shadow: {
        brand: 'rgba(51, 133, 255, 0.3)',
        brandStrong: 'rgba(51, 133, 255, 0.45)',
        brandGlow: 'rgba(51, 133, 255, 0.4)',
        secondary: 'rgba(58, 110, 165, 0.5)',
        dark: 'rgba(0, 0, 0, 0.2)',
    },
    badge: {
        text: '#FFFFFF',
        background: '#3A6EA5',
    },
    hero: {
        overlayStart: 'rgba(255, 255, 255, 0.3)',
        overlayEnd: 'rgba(255, 255, 255, 0.5)',
    }
}

// Backward-compat adapter: maps pre-refactor key names to current names.
// Only fires if the old key is present and the new key is absent.
// Keys the redesigned site no longer reads (brand.softHighlight,
// brand.backgroundPriceCardHeader, brand.backgroundNavbarMobile, text.price,
// text.priceAdjustment) and the legacy names that used to map onto them
// (brand.goldAlpha, text.priceColor) are STRIPPED here, so deepMerge — which
// copies unknown keys through — never carries them into the palette.
function normalizeTheme(theme) {
    if (!theme) return {}
    const t = JSON.parse(JSON.stringify(theme))

    if (t.brand) {
        if (t.brand.alpha !== undefined && t.brand.overlay === undefined)
            { t.brand.overlay = t.brand.alpha; delete t.brand.alpha }
        delete t.brand.goldAlpha
        delete t.brand.softHighlight
        delete t.brand.backgroundPriceCardHeader
        delete t.brand.backgroundNavbarMobile
    }

    if (t.background !== undefined && t.surface === undefined) {
        t.surface = t.background
        delete t.background
    }

    if (t.gradient) {
        t.surface = t.surface || {}
        if (t.gradient.wheat !== undefined && t.surface.gradientMid === undefined)
            t.surface.gradientMid = t.gradient.wheat
        if (t.gradient.burlywood !== undefined && t.surface.gradientBottom === undefined)
            t.surface.gradientBottom = t.gradient.burlywood
        delete t.gradient
    }

    if (t.text) {
        if (t.text.black !== undefined && t.text.strong === undefined)
            { t.text.strong = t.text.black; delete t.text.black }
        delete t.text.priceColor
        delete t.text.price
        delete t.text.priceAdjustment
    }

    if (t.border) {
        if (t.border.popular !== undefined && t.border.featured === undefined)
            { t.border.featured = t.border.popular; delete t.border.popular }
    }

    if (t.badge) {
        if (t.badge.textColor !== undefined && t.badge.text === undefined)
            { t.badge.text = t.badge.textColor; delete t.badge.textColor }
        if (t.badge.backgroundColor !== undefined && t.badge.background === undefined)
            { t.badge.background = t.badge.backgroundColor; delete t.badge.backgroundColor }
    }

    if (t.heroOverlay !== undefined && t.hero === undefined) {
        t.hero = {}
        if (t.heroOverlay.gradientStart !== undefined) t.hero.overlayStart = t.heroOverlay.gradientStart
        if (t.heroOverlay.gradientEnd !== undefined) t.hero.overlayEnd = t.heroOverlay.gradientEnd
        delete t.heroOverlay
    }

    return t
}

const palette = computed(() => deepMerge(defaultPalette, normalizeTheme(config.value?.theme)))

/**
 * Theme decoupling P7 (2026-09): the per-component slices this file used to
 * export — priceCardTheme, offerTheme, headerTheme, footerTheme,
 * atmosphereTheme, announcementTheme, globalTitle, globalBackground — are gone.
 * Every one of their consumers now reads a CSS custom property from
 * useThemeTokens.js, which is the only layer that derives anything from the
 * palette. This file's single job is to produce `palette`: merge the tenant's
 * stored theme (legacy key names migrated, dead keys stripped) onto the
 * defaults above.
 */
export const useTheme = () => ({ palette })
